<script lang="ts" setup>
import type { IRegisterParams } from '~/api/auth';
import NoticeText from './components/NoticeText.vue'
import MessageBox from './components/MessageBox.vue'
import ProjectSubmitNotice from './components/ProjectSubmitNotice.vue'
import type { FormInstance } from 'element-plus/es/components/form'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useReCaptchaV3 } from '@/utils/helper'
import useSeo from './seo_register'

const { t } = useI18n()
const useReCaptchaInstance = useReCaptcha()!
const { auth } = useApi()
const phoneCodeList = ref([
  {
    label: '+886',
    value: '+886'
  },
])
const passwordFlag = ref(false)
const eyes = computed(() => passwordFlag.value ? 'icon-eye' : 'icon-eye-slash')
const registerSuccess = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  role: 'regular',
  first_name: '',
  last_name: '',
  phone_code: '+886',
  phone: '',
  email: '',
  studio_name: '',
  studio_address: '',
  studio_url: '',
  password: '',
  confirm_password: '',
  is_subscribe: false,
  check_privacy: false
})

const dialogIWVisible = ref(false)
const dialogContributeVisible = ref(false)
const submitPedding = ref(false)

const handleSubmit = async () => {
  if (!formRef.value) return false
  // 避免double click
  if (submitPedding.value === false) {
    submitPedding.value = true
    await formRef.value.validate(async (valid, invalidFields) => {
      if (valid) {
        const token = await useReCaptchaV3(useReCaptchaInstance, 'inhome')

        const params = {
          role: form.role,
          first_name: form.first_name,
          last_name: form.last_name,
          phone_code: form.phone_code,
          phone: form.phone,
          email: form.email,
          studio_name: form.studio_name,
          studio_address: '',
          studio_url: '',
          password: form.password,
          confirm_password: form.confirm_password,
          google_recaptcha_token: token,
          is_subscribe: form.is_subscribe,
          is_subscribe_second: form.is_subscribe,
          is_subscribe_third: form.is_subscribe
        } as IRegisterParams

        const { data, pending, error } = await auth.register(params)
        if (!error.value) {
          // ElNotification({
          //   message: error.value.message,
          // })
        // } else {
          window.scrollTo(0, 0)
          registerSuccess.value = true
          setTimeout(() => {
            navigateTo({ path: '/' })
          }, 2000)
        }
      } else {
        ElNotification({
          message: t('update_error')
        })
        return false
      }
    })
    submitPedding.value = false
  }
}

useSeo()
</script>

<template>
  <section class="memberPage">
    <div class="memberPage__inner">
      <div v-if="!registerSuccess">
        <TitleTitle3 class="memberPage__mainTitle" :title="$t('member.register.title')" />
        <div class="memberPage__info">
          {{ $t('member.register.has_account') }}
          <nuxt-link to="/member/login" class="underline">{{ $t('member.register.to_login') }}</nuxt-link>
          <br />
          <div v-html="$t('member.register.need_submit_project')"></div>
        </div>
        <div class="memberPage__form">
          <el-form :model="form" ref="formRef">
            <div class="memberPage__form__item">
              <el-form-item class="memberPage__form__inline">
                <div class="memberPage__form__inline__left">
                  <FormTitle required class="memberPage__formTitle">{{ $t('member.register.resgister_type') }}</FormTitle>
                </div>
                <div class="memberPage__form__inline__right">
                  <el-radio-group v-model="form.role">
                    <el-radio label="regular" class="el-iw-radio">{{ $t('member.register.reader') }}</el-radio>
                    <el-radio label="designer" class="el-iw-radio">{{ $t('member.register.designer') }}</el-radio>
                  </el-radio-group>
                  <div class="memberPage__link ml-25" @click="dialogContributeVisible = true">{{ $t('member.register.information') }}</div>
                </div>
              </el-form-item>
            </div>

            <div class="memberPage__form__item">
              <el-form-item prop="last_name" :rules="[
                {
                  required: true,
                  message: $t('member.register.last_name') + ' is a required field'
                }
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.register.last_name') }}</FormTitle>
                <el-input v-model="form.last_name" class="el-iw-input" />
              </el-form-item>
            </div>

            <div class="memberPage__form__item">
              <el-form-item prop="first_name" :rules="[
                {
                  required: true,
                  message: $t('member.register.first_name') + ' is a required field'
                }
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.register.first_name') }}</FormTitle>
                <el-input v-model="form.first_name" class="el-iw-input" />
              </el-form-item>
            </div>

            <div class="memberPage__form__item" v-if="form.role === 'designer'">
              <el-form-item prop="studio_name" :rules="[
                {
                  required: true,
                  message: $t('member.register.studio_name') + ' is a required field'
                }
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.register.studio_name') }}</FormTitle>
                <el-input v-model="form.studio_name" class="el-iw-input" />
              </el-form-item>
            </div>

            <div class="memberPage__form__item">
              <el-form-item prop="phone" :rules="[
                {
                  required: true,
                  message: $t('member.register.phone') +' is a required field'
                }
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.register.phone') }}</FormTitle>
                <div class="memberPage__form__mobilePhoneItem">
                  <div class="memberPage__form__mobilePhoneItem__left">
                    <el-select v-model="form.phone_code" placeholder="Select Option" class="el-iw-select"
                      popper-class="el-iw-select-popper" :teleported="false">
                      <el-option v-for="item in phoneCodeList" :key="item.value" :label="item.label"
                        :value="item.value" />
                    </el-select>
                  </div>
                  <div class="memberPage__form__mobilePhoneItem__right">
                    <el-input v-model="form.phone" class="el-iw-input" />
                  </div>
                </div>
              </el-form-item>
            </div>

            <div class="memberPage__form__item">
              <el-form-item prop="email" :rules="[
                {
                  required: true,
                  message: $t('member.register.email') + ' is a required field'
                },
                {
                  type: 'email',
                  message: $t('email_validate'),
                },
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.register.email') }}</FormTitle>
                <el-input v-model="form.email" placeholder="Email" class="el-iw-input" />
              </el-form-item>
            </div>

            <div class="memberPage__form__item">
              <el-form-item prop="password" :rules="[
                {
                  required: true,
                  message:  $t('member.register.password') + ' is a required field'
                }
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.register.password') }}</FormTitle>
                <el-input v-model="form.password" :type="passwordFlag ? 'text' : 'password'" :placeholder="$t('password_validate')"
                  class="el-iw-input password-input">
                  <template #suffix>
                    <svg-icon :name="eyes" width="18" height="18" @click="passwordFlag = !passwordFlag" />
                  </template>
                </el-input>
              </el-form-item>
            </div>

            <div class="memberPage__form__item">
              <el-form-item prop="confirm_password" :rules="[
                {
                  required: true,
                  message: $t('member.register.check_password') + ' is a required field'
                }
              ]">
                <FormTitle required class="memberPage__formTitle">{{ $t('member.register.check_password') }}</FormTitle>
                <el-input v-model="form.confirm_password" :type="passwordFlag ? 'text' : 'password'"
                  class="el-iw-input" />
              </el-form-item>
            </div>

            <div class="memberPage__form__item">
              <NoticeText />
            </div>

            <div class="memberPage__form__item">
              <el-checkbox v-model="form.is_subscribe" class="el-iw-checkbox mb-10" :label="$t('member.register.subscribe1')" />

              <el-form-item prop="check_privacy" :rules="[
                { validator: validateAgreement, trigger: 'blur' }
              ]">
                <el-checkbox v-model="form.check_privacy" class="el-iw-checkbox">
                  {{ $t('member.register.subscribe2_1') }}
                  <span class="underline cursor-pointer" @click="dialogIWVisible = true">{{ $t('member.register.subscribe2_2') }}</span>
                  {{ $t('member.register.subscribe2_3') }}
                </el-checkbox>
              </el-form-item>
            </div>

            <div class="memberPage__form__item button">
              <ButtonButton1 :title="$t('member.register.submit')" @click="handleSubmit" :disabled="submitPedding" />
            </div>
          </el-form>
        </div>
      </div>
      <div v-else>
        <MessageBox>{{ $t('member.register_success')}}</MessageBox>
      </div>
    </div>

    <el-dialog v-model="dialogIWVisible" class="el-iw-dialog">
      <div class="textEditor dialogIW">
        IW會員制度
        <br />
        <br />
        ．讀者會員
        <br />
        <br />
        可收藏個人喜愛文章與選物，找尋文章瀏覽紀錄，及購買IW雜誌與選物。
        <br />
        <br />
        <br />
        ．設計師會員
        <br />
        <br />
        至會員專區點選「案例投稿」，可線上投稿建築空間作品。
        <br />
        注意：投稿作品須 1-2 週審核期，屆時將以 Email 通知是否採用。
      </div>
    </el-dialog>
    <el-dialog v-model="dialogContributeVisible" class="el-iw-dialog">
      <ProjectSubmitNotice />
    </el-dialog>
  </section>
</template>

<style lang="scss" scoped>
.dialogIW {
  line-height: 2;
}

.memberPage {
  &__mainTitle {
    margin-bottom: 45px;

    @include min-media(481) {
      font-size: 18px;
    }
  }

  &__info {
    margin-bottom: 45px;
    font-size: 14px;
    line-height: 2.2;
  }

  &__link {
    text-decoration: underline;
    font-size: 13px;
    cursor: pointer;

    @include max-media(480) {
      font-size: 10px;
    }
  }

  &__form {
    &__item {
      &.button {
        padding-top: 25px;
        text-align: center;

        :deep(button) {
          font-size: 13px;
        }
      }
    }
  }
}

.password-input {
  svg {
    cursor: pointer;
  }
}

:deep(.el-form-item.is-error .el-input__wrapper) {
  box-shadow: unset;
}
:deep(.el-form-item.is-error .el-select__wrapper) {
  box-shadow: unset;
}
</style>
